import type { ApiClient } from '@carbonwebmail/api-client'
import type { Reconciliation, StuckPageDto, StuckStatus } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * The messages no worker will move again, and the two ways out.
 *
 * One hook for both lists, because they differ in a word: `dead` is out of
 * retries and is replayed, `unknown` is ambiguous and is resolved. Everything
 * else — loading, the stale answer, the reload after acting — is identical, and
 * two copies would be two places for the reload to be forgotten.
 *
 * `stale` is the row having moved since the list was drawn: a reaper took it,
 * or somebody else acted first. It is reported as what happened rather than as
 * a failure, because nothing is wrong — the button simply had nothing to do.
 */

export type StuckState = AsyncState<StuckPageDto>

export interface Notice {
  readonly tone: 'ok' | 'danger'
  readonly text: string
}

export function useStuckMessages({
  client,
  status,
}: {
  readonly client: ApiClient
  readonly status: StuckStatus
}) {
  const load = useCallback(() => client.stuck(status, 1), [client, status])
  const { state, reload } = useLoaded<StuckPageDto>(
    load,
    `We could not load the ${status === 'dead' ? 'dead letters' : 'unresolved sends'}. Please try again in a moment.`,
  )

  const [notice, setNotice] = useState<Notice | undefined>(undefined)
  /** Which row is being acted on, so only its own buttons are disabled. */
  const [acting, setActing] = useState<string | undefined>(undefined)

  const act = useCallback(
    async (
      queueEntryId: string,
      run: () => Promise<{ status: 'done' | 'stale' }>,
      done: string,
    ) => {
      setNotice(undefined)
      setActing(queueEntryId)
      try {
        const outcome = await run()
        setNotice(
          outcome.status === 'done'
            ? { tone: 'ok', text: done }
            : {
                tone: 'danger',
                text: 'That message had already moved on, so nothing was changed.',
              },
        )
        await reload()
      } catch {
        setNotice({ tone: 'danger', text: 'We could not do that. Please try again in a moment.' })
      } finally {
        setActing(undefined)
      }
    },
    [reload],
  )

  const replay = useCallback(
    (queueEntryId: string) =>
      act(
        queueEntryId,
        () => client.replayDead(queueEntryId),
        'Back on the queue, with its attempts reset.',
      ),
    [act, client],
  )

  const resolve = useCallback(
    (queueEntryId: string, as: Reconciliation) =>
      act(
        queueEntryId,
        () => client.resolveUnknown(queueEntryId, as),
        as === 'delivered'
          ? 'Marked as delivered. Nothing was sent.'
          : 'Put back on the queue, and it will be sent again.',
      ),
    [act, client],
  )

  return { state, notice, acting, replay, resolve, retry: reload }
}
